import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import ExperienceTilePrices from 'components/Experiences/ExperienceTiles/ExperienceTilePrices'
import Divider from 'LuxKit/Divider'
import GeoContext from 'contexts/geoContext'
import { CurrencyProvider } from 'contexts/currencyContext'
import ExperienceOfferPageTicketSelection from 'components/Experiences/ExperienceOfferPage/ExperienceOfferPageTicketSelection'
import ExperienceOfferPageStateProvider from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateProvider'
import { mapExperienceToBookingView } from 'lib/experiences/experienceUtils'
import useTripExperiencePageState, { type TripExperienceInitialState } from '../../hooks/useTripExperiencePageState'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { appendContextTag } from 'tripPlanner/utils/contextTag'
import type { Coordinates } from 'tripPlanner/utils/chatItinerary'

export interface TripExperienceSelection {
  date: string | undefined
  time: string | undefined
  redemptionLocationId: string | undefined
  pickupPointId: string | undefined
  ticketModesMap: Record<string, string> | undefined
  tickets: Array<{ ticketId: string; count: number }>
}

function normaliseTicketCounts(ticketCounts: Record<string, number> | undefined): Record<string, number> {
  return Object.fromEntries(
    Object.entries(ticketCounts ?? {}).filter(([, count]) => count > 0),
  )
}

function recordsEqual(left: Record<string, string | number> | undefined, right: Record<string, string | number> | undefined): boolean {
  const normalisedLeft = Object.fromEntries(Object.entries(left ?? {}).filter(([, value]) => value !== undefined && value !== ''))
  const normalisedRight = Object.fromEntries(Object.entries(right ?? {}).filter(([, value]) => value !== undefined && value !== ''))
  const leftEntries = Object.entries(normalisedLeft)
  const rightEntries = Object.entries(normalisedRight)
  return leftEntries.length === rightEntries.length && leftEntries.every(([key, value]) => normalisedRight[key] === value)
}

function getSingleOptionId<TOption extends { id: string }>(options: Array<TOption> | undefined): string | undefined {
  return options?.length === 1 ? options[0].id : undefined
}

const PricingAvailabilitySection = styled.div`
  scroll-margin-top: ${rem(24)};
`

interface Props {
  price?: number
  memberPrice?: number
  memberDiscountPercent?: number
  value?: number
  currencyCode: string
  deeplinkUrl: string
  isSoldOut: boolean
  experienceName: string
  offerId: string
  coordinates?: Coordinates
  standaloneOffer?: App.ExperienceOffer
  initialPageState?: TripExperienceInitialState
  onSaveToTrip?: (selection: TripExperienceSelection) => void
  itineraryData?: TripItineraryData
  onAddToTrip?: (message: string, options?: SendMessageOptions) => void
  onAfterAddToTrip?: () => void
  scrollToPricing?: boolean
  checkoutSource?: App.CheckoutSource
}

function ExperienceDetailPricing({
  price,
  memberPrice,
  memberDiscountPercent,
  value,
  currencyCode,
  deeplinkUrl,
  isSoldOut,
  experienceName,
  offerId,
  coordinates,
  standaloneOffer,
  initialPageState,
  onSaveToTrip,
  itineraryData,
  onAddToTrip,
  onAfterAddToTrip,
  scrollToPricing,
  checkoutSource,
}: Props) {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const pricingSectionRef = useRef<HTMLDivElement>(null)
  const href = `/${geo.currentRegionCode}${deeplinkUrl}`

  const hasDiscount = value !== undefined && price !== undefined && value > price
  const discountPercent = hasDiscount ? Math.round(((value - price) / value) * 100) : 0

  const isTransfer = standaloneOffer?.productType === 'transfer'

  const [pageState, pageDispatch] = useTripExperiencePageState(initialPageState, standaloneOffer)
  const isSaveDisabled = Object.values(pageState.ticketCounts).every(c => c === 0)
  const experienceView = useMemo(
    () => (standaloneOffer && !isTransfer) ? mapExperienceToBookingView(standaloneOffer) : undefined,
    [standaloneOffer, isTransfer],
  )
  const isSelectionAlreadySaved = useMemo(() => {
    if (!onSaveToTrip || !initialPageState) return false
    const initialRedemptionLocationId = initialPageState.redemptionLocationId ?? getSingleOptionId(standaloneOffer?.redemptionLocations)
    const initialPickupPointId = initialPageState.pickupPointId ?? getSingleOptionId(standaloneOffer?.pickupPoints)
    return recordsEqual(normaliseTicketCounts(pageState.ticketCounts), normaliseTicketCounts(initialPageState.ticketCounts)) &&
      (standaloneOffer?.hasCalendar ? pageState.date === initialPageState.date : true) &&
      (standaloneOffer?.hasTimeslots ? pageState.time === initialPageState.time : true) &&
      pageState.redemptionLocation?.id === initialRedemptionLocationId &&
      pageState.pickupPoint?.id === initialPickupPointId &&
      recordsEqual(pageState.ticketModesMap, initialPageState.ticketModesMap)
  }, [initialPageState, onSaveToTrip, pageState, standaloneOffer?.hasCalendar, standaloneOffer?.hasTimeslots, standaloneOffer?.pickupPoints, standaloneOffer?.redemptionLocations])

  const handleCheckAvailability = useCallback(() => {
    pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  useEffect(() => {
    if (!scrollToPricing) return
    pricingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, [])

  const handleSaveToTrip = useCallback(() => {
    if (!onSaveToTrip) return
    const tickets = Object.entries(pageState.ticketCounts)
      .filter(([, count]) => count > 0)
      .map(([ticketId, count]) => ({ ticketId, count }))
    onSaveToTrip({
      date: standaloneOffer?.hasCalendar ? pageState.date : undefined,
      time: standaloneOffer?.hasTimeslots ? pageState.time : undefined,
      redemptionLocationId: pageState.redemptionLocation?.id,
      pickupPointId: pageState.pickupPoint?.id,
      ticketModesMap: pageState.ticketModesMap,
      tickets,
    })
    onAfterAddToTrip?.()
  }, [onSaveToTrip, onAfterAddToTrip, pageState, standaloneOffer])

  const handleAddToTripMessage = useCallback((message: string, options?: SendMessageOptions) => {
    if (!onAddToTrip) return
    const ticketParts = Object.entries(pageState.ticketCounts)
      .filter(([, count]) => count > 0)
      .map(([ticketId, count]) => `${count}x${ticketId}`)
    const contextLines = [
      standaloneOffer ? `experience_id=${standaloneOffer.id}` : '',
      // Only include date/time when the ticket form has selected calendar/timeslot values.
      standaloneOffer?.hasCalendar && pageState.date ? `date=${pageState.date}` : '',
      standaloneOffer?.hasTimeslots && pageState.time ? `time=${pageState.time}` : '',
      ticketParts.length ? `tickets=${ticketParts.join(',')}` : '',
      pageState.redemptionLocation?.id ? `redemption_location_id=${pageState.redemptionLocation.id}` : '',
      pageState.pickupPoint?.id ? `pickup_point_id=${pageState.pickupPoint.id}` : '',
      pageState.ticketModesMap ? `ticket_modes=${JSON.stringify(pageState.ticketModesMap)}` : '',
    ].filter(Boolean)
    const enriched = contextLines.length > 0
      ? appendContextTag(message, contextLines.join(', '), 'Experience ticket selection')
      : message
    onAddToTrip(enriched, options)
  }, [onAddToTrip, pageState, standaloneOffer])

  const addToTripAction = itineraryData && onAddToTrip ? (
    <AddToTripAction
      itineraryData={itineraryData}
      onSendMessage={handleAddToTripMessage}
      placeName={experienceName}
      offerId={offerId}
      offerType="experience"
      coordinates={coordinates}
      variant="button"
      buttonKind="secondary"
      onAfterAction={onAfterAddToTrip}
    />
  ) : undefined

  const checkAvailabilityLabel = intl.formatMessage({
    id: 'cz6O9A05fQXwuzafKdDO',
    defaultMessage: 'Check availability',
    description: 'Button label to scroll to pricing and availability section for experience',
  })

  const bookNowLabel = intl.formatMessage({
    id: 'azlPTkuZgo66yhqZs2e4',
    defaultMessage: 'Book now',
    description: 'Button label to book an experience offer on the experience detail drawer',
  })

  const soldOutLabel = intl.formatMessage({
    id: 'r1Z0H_THzeGe1u2EN68c',
    defaultMessage: 'Sold out',
    description: 'Label shown when experience offer is sold out on the experience detail drawer',
  })

  const pricingHeading = intl.formatMessage({
    id: 'u_VwtpzWYhCsiddc0cPX',
    defaultMessage: 'Pricing & availability',
    description: 'Section heading for experience pricing and availability',
  })

  const selectTicketsLabel = intl.formatMessage({
    id: 'fXDj63mPJRZgfATFQUi5',
    defaultMessage: 'Add to trip',
    description: 'Button label to add experience ticket choices to the trip planner itinerary',
  })
  const selectedTicketsLabel = intl.formatMessage({
    id: 'YpI7gGyG-sWVfcFqL9Vr',
    defaultMessage: 'Already in trip',
    description: 'Disabled button label shown when the current experience ticket choices are already in the trip planner itinerary',
  })

  const saveToTripButton = onSaveToTrip ? (
    <TextButton
      kind="secondary"
      size="large"
      onClick={handleSaveToTrip}
      disabled={isSaveDisabled || isSelectionAlreadySaved}
      startIcon={isSelectionAlreadySaved ? <LineCheckIcon /> : <LinePlusIcon />}
    >
      {isSelectionAlreadySaved ? selectedTicketsLabel : selectTicketsLabel}
    </TextButton>
  ) : undefined

  return (
    <CurrencyProvider currency={currencyCode}>
      <Group direction="vertical" gap={24}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <Group direction="vertical" gap={4}>
            {price !== undefined && (
              <ExperienceTilePrices
                totalPrice={price}
                totalMemberPrice={memberPrice}
                totalGrossPrice={value}
                discountPercentage={discountPercent}
                discountMemberPercentage={memberDiscountPercent}
                isFromPrice
                ticketUnitLabel="person"
                size="L"
              />
            )}
          </Group>
          <TextButton
            kind="primary"
            size="medium"
            onClick={handleCheckAvailability}
            disabled={isSoldOut}
          >
            {isSoldOut ? soldOutLabel : checkAvailabilityLabel}
          </TextButton>
        </Group>

        <Divider kind="secondary" />

        <Group direction="vertical" gap={8}>
          <Heading variant="heading5">
            {intl.formatMessage({
              id: 'pYLZeh0rjjWTE-Ak6xb9',
              defaultMessage: 'Why it fits your trip',
              description: 'Section heading for why the experience fits the user trip',
            })}
          </Heading>
          <BodyText variant="medium" colour="light-foreground-alt">
            {intl.formatMessage({
              id: '-CHvPsdyvAZP0hXoB9hn',
              defaultMessage: "This experience is a great match for your trip — it's conveniently located, highly rated by travellers, and offers a memorable way to explore the destination. Whether you're after culture, adventure, or relaxation, this activity adds something special to your itinerary.",
              description: 'Generic description explaining why the experience fits the user trip',
            })}
          </BodyText>
        </Group>

        <Divider kind="secondary" />

        <PricingAvailabilitySection ref={pricingSectionRef}>
          <Group direction="vertical" gap={16}>
            <Heading variant="heading5">{pricingHeading}</Heading>

            {experienceView && (
              <ExperienceOfferPageStateProvider state={pageState} dispatch={pageDispatch}>
                <ExperienceOfferPageTicketSelection
                  experienceView={experienceView}
                  secondaryAction={saveToTripButton ?? addToTripAction}
                  showAddToCartAction={false}
                  checkoutSource={checkoutSource}
                  preserveSelectedDateWhenDateOptionsEmpty
                />
              </ExperienceOfferPageStateProvider>
            )}

            {!experienceView && !isSoldOut && (
              <Group direction="horizontal" horizontalAlign="end" gap={8} verticalAlign="center">
                {addToTripAction}
                <TextButton
                  kind="primary"
                  size="medium"
                  endIcon={<LineExternalLinkAltIcon />}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {bookNowLabel}
                </TextButton>
              </Group>
            )}
          </Group>
        </PricingAvailabilitySection>
      </Group>
    </CurrencyProvider>
  )
}

export default ExperienceDetailPricing
